/**
 * What happens between "the server issued tokens" and "the user is in".
 *
 * ONE COPY FOR EVERY SIGN-IN PAGE
 * -------------------------------
 * Login (email and Google), the 2FA page and Sign up each finished a sign-in on
 * their own. Login and 2FA carried near-identical copies of this logic, and Sign
 * up's Google path had none at all — so an account linked to no institute was
 * stopped on one page and walked straight in on the other. The rule now lives
 * here, and each page only says where to go and what to announce.
 *
 * THE RULE
 * --------
 * Ask the server what this account may sign in as (`useLoginContexts`), then:
 *
 *   one context      select it silently, so the token carries the institute
 *   several          show the picker
 *   none, via link   open the session and return to the link — see linkReturn
 *   none, direct     "No access yet", and hand the tokens and slot back
 *   unavailable      finish as before; never block a sign-in on this question
 *
 * The same "none" rule applies to a session that is ALREADY stored when a
 * sign-in page opens (`resumeExistingSession`). Without it, anyone who signed in
 * through a link could later open the app directly and skip the check.
 */

import { useCallback, useRef, useState } from "react";

import { abandonSession } from "@/services/sessionSignout";
import { organizationLandingPath } from "@/services/organizationSession";

import {
  clearLegacyReturnKeys,
  clearLinkReturn,
  isLinkReturnTarget,
  nextSignInStep,
} from "../linkReturn";
import { currentSessionEmail, readLastContext, rememberLastContext } from "./lastContext";
import type { LoginContext } from "./types";
import { useLoginContexts } from "./useLoginContexts";

export interface SignInNotice {
  title: string;
  description: string;
}

export interface UseCompleteSignInOptions {
  /** Where this sign-in is headed, already sanitised. Read when it finishes. */
  getReturnTarget: () => string;
  /** Open the session: announce `notice` if given, then go to `target`. */
  onFinish: (target: string, notice: SignInNotice | null) => void;
  /** "Back to sign in" on the no-access dialog. Defaults to just closing it. */
  onNoAccessClosed?: () => void;
}

export interface UseCompleteSignInResult {
  /** After a fresh sign-in has stored its tokens. */
  completeSignIn: (
    email: string | null | undefined,
    notice: SignInNotice | null,
  ) => Promise<void>;
  /** A session was already stored when the page opened. */
  resumeExistingSession: () => Promise<void>;
  noAccessDialogProps: {
    open: boolean;
    email: string | null;
    onOpenChange: (open: boolean) => void;
  };
  contextDialogProps: {
    open: boolean;
    contexts: LoginContext[];
    lastUsedUid: string | null;
    loading: boolean;
    error: string;
    onSelect: (membershipUid: string) => void;
    onOpenChange: (open: boolean) => void;
  };
}

export const useCompleteSignIn = (
  options: UseCompleteSignInOptions,
): UseCompleteSignInResult => {
  // The pages rebuild these callbacks every render. Read the latest through a
  // ref so the functions returned below stay stable — a stale copy of
  // getReturnTarget is exactly how a sign-in would land on the wrong page.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const { contexts, loading, error, fetchContexts, choose, reset } = useLoginContexts();

  const [showContextPicker, setShowContextPicker] = useState(false);
  const [showNoAccess, setShowNoAccess] = useState(false);
  const [noAccessEmail, setNoAccessEmail] = useState<string | null>(null);
  const [lastUsedUid, setLastUsedUid] = useState<string | null>(null);

  // Held while the picker is open, so the choice finishes with the same notice
  // and remembers the choice against the right email.
  const pendingNoticeRef = useRef<SignInNotice | null>(null);
  const pendingEmailRef = useRef<string | null>(null);
  // One check at a time: a page effect re-running while a sign-in is being
  // decided must not start a second, competing one.
  const busyRef = useRef(false);

  const finish = useCallback((notice: SignInNotice | null, landing?: string | null) => {
    pendingNoticeRef.current = null;
    const target = landing || optionsRef.current.getReturnTarget();
    clearLinkReturn();
    optionsRef.current.onFinish(target, notice);
  }, []);

  const denyAccess = useCallback((email: string | null) => {
    pendingNoticeRef.current = null;
    setNoAccessEmail(email);
    setShowNoAccess(true);
    // Nothing was entered, so nothing about this attempt should outlive it —
    // least of all a link hand-off that would wave the next direct sign-in in.
    clearLinkReturn();
    clearLegacyReturnKeys();
    // The tokens are stored and the server has claimed this device's slot.
    // Hand both back, or a reload walks into the app and the next sign-in is
    // told the account is "already signed in elsewhere".
    void abandonSession();
  }, []);

  const selectContext = useCallback(
    async (membershipUid: string) => {
      const ok = await choose(membershipUid);
      if (!ok) return; // the error is shown inside the picker

      rememberLastContext(pendingEmailRef.current ?? currentSessionEmail(), membershipUid);
      // An organization is run from inside an institute: go straight there,
      // so the platform shell never flashes on the way (organizationSession).
      const landing = await organizationLandingPath(pendingEmailRef.current ?? currentSessionEmail());
      setShowContextPicker(false);
      finish(pendingNoticeRef.current, landing);
    },
    [choose, finish],
  );

  const completeSignIn = useCallback(
    async (email: string | null | undefined, notice: SignInNotice | null) => {
      busyRef.current = true;
      try {
        const { outcome, contexts: fetched } = await fetchContexts();
        pendingNoticeRef.current = notice;
        pendingEmailRef.current = email ?? null;

        // The list arrives WITH the outcome. `contexts` state would still hold
        // the previous render's value here, which silently skipped selection.
        switch (nextSignInStep(outcome, optionsRef.current.getReturnTarget())) {
          case "deny":
            denyAccess(email ?? null);
            return;
          case "select":
            if (fetched[0]) {
              await selectContext(fetched[0].membership_uid);
              return;
            }
            finish(notice);
            return;
          case "choose":
            setLastUsedUid(readLastContext(email));
            setShowContextPicker(true);
            return;
          default:
            finish(notice);
        }
      } finally {
        busyRef.current = false;
      }
    },
    [fetchContexts, denyAccess, selectContext, finish],
  );

  const resumeExistingSession = useCallback(async () => {
    if (busyRef.current) return;

    // Back to a link: go, exactly as before. The link page does its own checks.
    if (isLinkReturnTarget(optionsRef.current.getReturnTarget())) {
      finish(null);
      return;
    }

    busyRef.current = true;
    try {
      const { outcome } = await fetchContexts();
      // Only "none" changes anything. The institute was already chosen when
      // this session was opened, so one or several contexts just continue.
      if (outcome === "none") {
        denyAccess(currentSessionEmail());
        return;
      }
      finish(null);
    } finally {
      busyRef.current = false;
    }
  }, [fetchContexts, denyAccess, finish]);

  // Backing out of the picker abandons the sign-in rather than half-opening a
  // session — including the stored tokens and the server-side slot.
  const closeContextPicker = useCallback(() => {
    setShowContextPicker(false);
    pendingNoticeRef.current = null;
    reset();
    void abandonSession();
  }, [reset]);

  return {
    completeSignIn,
    resumeExistingSession,
    noAccessDialogProps: {
      open: showNoAccess,
      email: noAccessEmail,
      onOpenChange: (next: boolean) => {
        if (next) return;
        setShowNoAccess(false);
        optionsRef.current.onNoAccessClosed?.();
      },
    },
    contextDialogProps: {
      open: showContextPicker,
      contexts,
      lastUsedUid,
      loading,
      error,
      onSelect: (uid: string) => {
        void selectContext(uid);
      },
      onOpenChange: (next: boolean) => {
        if (!next) closeContextPicker();
      },
    },
  };
};

export default useCompleteSignIn;
